import type { WorkspaceRail } from "@/context/SidebarContext";
import { AutomationsRail } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/automations/AutomationsRail";
import { ChatsAllRail } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/chats/ChatsAllRail";
import { ConnectorsRail } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/connectors/ConnectorsRail";
import { RailFilesDropHighlightProvider } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/RailFilesDropHighlight";
import "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railSelectionChrome.css";
import { RailSelectionProvider } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { SkillsRail } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/SkillsRail";
import { WorkspacesRail } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/workspaces/WorkspacesRail";
import type { Flags } from "@/providers/LaunchDarklyProvider";
import { useFlags } from "launchdarkly-react-client-sdk";

const RAIL_LABELS: Record<WorkspaceRail, string> = {
  workspaces: "Workspaces",
  chats: "Chats",
  skills: "Skills",
  automations: "Automations",
  connectors: "Connectors",
};

export function WorkspaceRailContent({ rail }: { rail: WorkspaceRail }) {
  // RailSelectionProvider holds Finder-style multi-select for Workspaces /
  // Skills / nested file rows. resetKey clears when the active rail changes.
  // RailFilesDropHighlightProvider tracks the OS file-drag target under the
  // pointer so every `RailOsFileDropZone` can light up during a native drag.
  return (
    <RailFilesDropHighlightProvider>
      <RailSelectionProvider resetKey={rail}>
        <WorkspaceRailContentInner rail={rail} />
      </RailSelectionProvider>
    </RailFilesDropHighlightProvider>
  );
}

function WorkspaceRailContentInner({ rail }: { rail: WorkspaceRail }) {
  const flags = useFlags<Flags>();

  if (rail === "workspaces") {
    return <WorkspacesRail />;
  }

  if (rail === "chats") {
    return <ChatsAllRail />;
  }

  if (rail === "skills") {
    return <SkillsRail />;
  }

  // Flag-guarded here too: if the LD flag flips off mid-session while this
  // rail is active, the view degrades to the placeholder below.
  if (rail === "automations" && flags.automations) {
    return <AutomationsRail />;
  }

  if (rail === "connectors") {
    return <ConnectorsRail />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col px-2 py-2">
      <p className="px-2 py-1 font-mono text-2xs font-medium uppercase tracking-[0.08em] text-n-50">
        {RAIL_LABELS[rail]}
      </p>
      <div className="flex flex-1 items-center justify-center px-4 py-8">
        <p className="text-center text-sm text-n-40">Coming soon</p>
      </div>
    </div>
  );
}
